import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { axiosInstance } from '../../utils/common';

interface ErrorLog {
  timestamp: number;
  message: string;
  labels: Record<string, string>;
  severity: string;
  level?: string;
}

interface Toast extends ErrorLog {
  id: string;
  visible: boolean;
  parsedData?: any;
}

interface ConfirmToastPopupProps {
  isOpen: boolean;
  onClose: () => void;
  toast: Toast | null;
  selectedCustomerName: string;
  selectedSite: string;
}

const ConfirmToastPopup: React.FC<ConfirmToastPopupProps> = ({
  isOpen,
  onClose,
  toast: toastMessage,
  selectedCustomerName,
  selectedSite
}) => {
  const [personName, setPersonName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to send Loki Clear command
  const sendLokiClearCommand = async (toastData: Toast) => {
    try {
      const lokiPushUrl = import.meta.env.VITE_LOKI_PUSH_URL;
      if (!lokiPushUrl) {
        console.error('VITE_LOKI_PUSH_URL not configured in environment');
        return false;
      }

      const data = toastData.parsedData || {};

      // Get current time in nanoseconds
      const currentTimeNano = `${Date.now()}000000`;

      // Extract labels from toast
      const application = toastData.labels?.application || '';
      const component = toastData.labels?.component || '';
      const environment = toastData.labels?.environment || 'production';
      const namespace = toastData.labels?.namespace || 'Toast';

      // Extract data from parsedData
      const sequenceId = data.sequence_id || '';
      const level = data.level || toastData.severity || 'ERROR';
      const customer = data.customer || selectedCustomerName;
      const location = data.location;

      // Build the message object
      const messageObj: any = {
        toast_command: 'Clear',
        sequence_id: sequenceId,
        level: level,
        timestamp: currentTimeNano
      };

      // Add customer if it exists
      if (customer) {
        messageObj.customer = customer;
      }

      // Add location if it exists
      if (location) {
        messageObj.location = location;
      }

      // Build the request payload
      const payload = {
        streams: [
          {
            stream: {
              application: application,
              component: component,
              environment: environment,
              namespace: namespace
            },
            values: [
              [
                currentTimeNano,
                JSON.stringify(messageObj)
              ]
            ]
          }
        ]
      };

 

      const response = await fetch(`${lokiPushUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Loki API error: ${response.status} ${response.statusText}`);
      }

      console.log('Loki Clear command sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending Loki Clear command:', error);
      return false;
    }
  };

  const handleCreateTicket = async () => {
    if (!personName.trim()) {
      toast.warning('Please enter your name', { position: 'top-right', autoClose: 3000 });
      return;
    }

    if (!toastMessage) return;

    setIsSubmitting(true);

    try {
      const data = toastMessage.parsedData || {};
      const ticketData = {
        person_name: personName,
        notes: notes,
        toast: {
          sequence_id: data.sequence_id,
          message: data.message,
          severity: toastMessage.severity,
          timestamp: new Date(toastMessage.timestamp).toISOString(),
          module: data.module,
          function: data.function,
          line_number: data.line_number
        },
        customer: selectedCustomerName,
        site: selectedSite,
        created_at: new Date().toISOString(),
        custom_fields: {
          cf_customer: selectedCustomerName,
          cf_comment_loki: data.message || 'Error message',
          cf_name_loki: toastMessage.labels?.component || toastMessage.labels?.application || '',
          cf_site549576: selectedSite
        }
      };

      

      // // Call API to create Freshdesk ticket
      // const response = await axiosInstance.post('toast/create_freshdesk_ticket/', ticketData, {
      //   headers: { customer: selectedCustomerName }
      // });
      
      if(true){
      // if (response.status === 201 || response.status === 200 || true) {
        toast.success(`Ticket created successfully by ${personName}`, { position: 'top-right', autoClose: 3000 });

        // If Freshdesk ticket creation was successful, send Loki Clear command
        const lokiSuccess = await sendLokiClearCommand(toastMessage);

        if (lokiSuccess) {
          console.log('Toast cleared successfully in Loki');
        } else {
          console.warn('Failed to clear toast in Loki, but ticket was created');
        }
      } else {
        toast.error('Failed to create ticket. Please try again.', { position: 'top-right', autoClose: 5000 });
      }

      // Reset form and close
      setPersonName('');
      setNotes('');
      onClose();
    } catch (error) {
      console.error('Error in handleCreateTicket:', error);
      toast.error('An error occurred while creating the ticket', { position: 'top-right', autoClose: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPersonName('');
    setNotes('');
    onClose();
  };

  if (!isOpen || !toastMessage) return null;

  const data = toastMessage.parsedData || {};

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2001,
        padding: 20
      }}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          maxWidth: 500,
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: '#111827'
          }}>
            Create Ticket
          </h2>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: 13,
            color: '#6b7280'
          }}>
            Please provide your details and any notes
          </p>
        </div>

        {/* Form Content */}
        <div style={{ padding: '24px' }}>
          {/* Error Details Summary */}
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: 12,
            marginBottom: 20
          }}>
            <div style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#991b1b',
              marginBottom: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {toastMessage.severity}
            </div>
            <div style={{
              fontSize: 13,
              color: '#7f1d1d',
              lineHeight: 1.4
            }}>
              {data.message || 'Error message'}
            </div>
            {data.sequence_id && (
              <div style={{
                fontSize: 11,
                color: '#9ca3af',
                marginTop: 6
              }}>
                ID: {data.sequence_id}
              </div>
            )}
          </div>

          {/* Person Name Input */}
          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              color: '#374151',
              marginBottom: 6
            }}>
              Your Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder="Enter your name"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                fontFamily: 'system-ui, -apple-system, sans-serif',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            />
          </div>

          {/* Notes Textarea */}
          <div style={{ marginBottom: 0 }}>
            <label style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              color: '#374151',
              marginBottom: 6
            }}>
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes or context..."
              rows={4}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                fontFamily: 'system-ui, -apple-system, sans-serif',
                outline: 'none',
                resize: 'vertical',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12
        }}>
          <button
            onClick={handleClose}
            style={{
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreateTicket}
            disabled={isSubmitting}
            style={{
              background: isSubmitting ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 500,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s ease',
              opacity: isSubmitting ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.background = '#2563eb';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.background = '#3b82f6';
              }
            }}
          >
            {isSubmitting ? 'Processing...' : 'Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmToastPopup;
